import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play, Users, UserX, CalendarDays, Dumbbell, LayoutGrid, AlertTriangle } from "lucide-react";
import { api, fmtDate, label } from "../../lib/api";
import { LIVE_STATUSES } from "../../lib/portals";
import { Panel, Empty, Spinner, Crest, StatusBadge, DemoTag, Avatar } from "../../components/kit";
import { Stat } from "../referee/RefereeDashboard";
import { useCoachTeams } from "./Tactics";

const DONE = ["full_time", "reports_pending", "reports_submitted", "under_review", "validated", "archived"];
const ICONS = { "Available Players": Users, Suspended: UserX, "Upcoming Fixtures": CalendarDays, "Training Sessions": Dumbbell };
const LINKS = { "Available Players": "/coach/teams", Suspended: "/league/disciplinary", "Upcoming Fixtures": "/coach/matches", "Training Sessions": "/coach/training" };

const result = (m, tids) => {
  const home = tids.includes(m.home.team_id);
  const [a, b] = home ? [m.score.home, m.score.away] : [m.score.away, m.score.home];
  return a > b ? "W" : a < b ? "L" : "D";
};

function NextMatch({ m, team, needsSheet }) {
  const live = LIVE_STATUSES.includes(m.status);
  const to = `/coach/tactics?team=${team.id}&match=${m.id}`;
  return (
    <section className="tro-card relative overflow-hidden" data-testid="coach-next-match">
      <div className="absolute inset-0 tro-metal opacity-60" />
      <div className="relative p-4 md:p-6">
        <div className="flex flex-wrap items-center gap-2"><span className="tro-label">{live ? "Live now" : "Upcoming match"}</span><StatusBadge status={m.status} />{m.demo && <DemoTag />}</div>
        <div className="mt-4 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
          <div className="flex min-w-0 flex-col items-center gap-2 text-center"><Crest club={m.home} size={56} /><b className="truncate text-sm md:text-lg">{m.home.name}</b></div>
          <div className="text-center"><div className="font-mono text-3xl font-extrabold">{live ? `${m.score.home}–${m.score.away}` : "vs"}</div><div className="text-[11px] font-bold uppercase text-[#6C757D]">{live ? label(m.status) : fmtDate(m.kickoff)}</div></div>
          <div className="flex min-w-0 flex-col items-center gap-2 text-center"><Crest club={m.away} size={56} /><b className="truncate text-sm md:text-lg">{m.away.name}</b></div>
        </div>
        <div className="mt-3 text-center text-xs text-[#343A40]">{m.competition?.name} · {m.venue}</div>
        <div className="mt-3 flex justify-center" data-testid="lineup-status">
          <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold uppercase ${needsSheet ? "bg-[#FFD400] text-[#171B20]" : "bg-[#35865C] text-white"}`}>{needsSheet ? "Team sheet not submitted" : live ? "Official lineup locked" : "Team sheet submitted"}</span>
        </div>
        <Link to={to} data-testid={live ? "open-live-board-btn" : "prepare-team-sheet-btn"}
          className={`mt-4 flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[14px] text-base font-extrabold uppercase tracking-wide text-white transition-transform active:scale-[0.98] ${live ? "bg-[#F02030]" : "bg-[#171B20]"}`}>
          {live ? <><Play className="h-5 w-5" />Open live board</> : <>{needsSheet ? "Prepare team sheet" : "Open tactical board"}<ArrowRight className="h-5 w-5" /></>}
        </Link>
      </div>
    </section>
  );
}

export default function CoachDashboard() {
  const teams = useCoachTeams();
  const [d, setD] = useState(null);
  const [list, setList] = useState([]);
  const [disc, setDisc] = useState([]);
  useEffect(() => {
    api.get("/dashboard/coach").then((r) => setD(r.data));
    api.get("/matches", { params: { scope: "mine" } }).then((r) => setList(r.data)).catch(() => {});
    api.get("/disciplinary").then((r) => setDisc(r.data)).catch(() => {});
  }, []);
  if (!d) return <Spinner />;
  const tids = teams.map((t) => t.id);
  const team = teams[0];
  const mine = list.filter((m) => tids.includes(m.home.team_id) || tids.includes(m.away.team_id));
  const next = (d.upcoming || []).find((m) => LIVE_STATUSES.includes(m.status)) || [...(d.upcoming || [])].sort((a, b) => a.kickoff.localeCompare(b.kickoff))[0];
  const recent = mine.filter((m) => DONE.includes(m.status)).sort((a, b) => b.kickoff.localeCompare(a.kickoff)).slice(0, 5);
  const dTeam = disc.filter((x) => tids.includes(x.team_id)).slice(0, 5);
  return (
    <div className="space-y-4" data-testid="coach-dashboard">
      <div className="flex items-center gap-3" data-testid="coach-team-card">
        {team && <Crest club={{ ...team, name: team.name, logo: team.logo }} size={48} />}
        <div className="min-w-0"><div className="tro-label">Coach Dashboard</div><h1 className="truncate text-2xl font-extrabold tracking-tight sm:text-3xl">{team?.name || "My team"}</h1><div className="text-xs text-[#6C757D]">{team?.category !== team?.name && team?.category} {teams.length > 1 ? `· +${teams.length - 1} more team(s)` : ""}</div></div>
      </div>
      {next && team ? <NextMatch m={next} team={team} needsSheet={(d.tasks || []).some((t) => t.includes(next.match_number))} /> : <Empty testid="no-upcoming" text="No upcoming matches." />}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {d.cards.map((c) => <Stat key={c.label} icon={ICONS[c.label] || LayoutGrid} l={c.label} v={c.value} to={LINKS[c.label] || "/coach"} testid={`coach-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Recent results" testid="coach-recent-results">
          {!recent.length && <Empty text="No completed matches yet." />}
          <div className="space-y-2">{recent.map((m) => {
            const r = result(m, tids);
            return (
              <Link key={m.id} to={`/matches/${m.id}`} data-testid={`result-${m.match_number}`} className="flex items-center gap-3 rounded-[14px] border border-[#E7EAEE] p-2.5 hover:bg-[#F7F8FA]">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] text-sm font-extrabold ${r === "W" ? "bg-[#35865C] text-white" : r === "L" ? "bg-[#F02030] text-white" : "bg-[#E7EAEE]"}`}>{r}</span>
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">{m.home.short} {m.score.home}–{m.score.away} {m.away.short}</div><div className="truncate text-xs text-[#6C757D]">{fmtDate(m.kickoff)} · {label(m.status)}</div></div>
              </Link>
            );
          })}</div>
        </Panel>
        <Panel title="Disciplinary notifications" testid="coach-discipline">
          {!dTeam.length && <Empty text="No disciplinary matters for your team." />}
          <div className="space-y-2">{dTeam.map((x) => (
            <div key={x.id} className="flex items-center gap-3 rounded-[14px] border border-[#E7EAEE] p-2.5" data-testid={`coach-disc-${x.id}`}>
              <Avatar player={x.player} size={36} />
              <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold">#{x.player?.number} {x.player?.name}</div><div className="truncate text-xs text-[#6C757D]">{label(x.type)} · {x.match_number} · ban {x.suspension_matches}</div></div>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-extrabold uppercase ${x.status === "suspended" ? "bg-[#F02030] text-white" : "bg-[#E7EAEE]"}`}>{label(x.status)}</span>
            </div>
          ))}</div>
        </Panel>
      </div>
      {(d.tasks || []).length > 0 && <Panel title="Action required" testid="coach-tasks">
        {d.tasks.map((t) => <div key={t} className="flex items-center gap-2 py-1 text-sm font-semibold"><AlertTriangle className="h-4 w-4 text-[#B38F00]" />{t}</div>)}
      </Panel>}
    </div>
  );
}
